// Library Methods
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

// Components
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import Loader from "src/components/shared/loaders/Loader/Loader";

// Utils
import getUserFromToken from "src/utils/token/tokenHelper";

// Styles
import { PageContainerWrapper } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Services
import { getUserSettings } from "src/services/users/api";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import useLocalStorage from "src/hooks/storage/useLocalStorage";

// Services and API
import { getClinics } from "src/services/clinics/api";
import { login } from "src/services/auth/api";

// Types
import { ClinicType } from "src/services/clinics/types";

const Callback = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useTokenAccess();
  const { setClinicId } = useClinicContext();
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  const params = new URLSearchParams(window.location.search);
  const [, setWelcomeModal] = useLocalStorage("displayWelcomeModal", false);

  const handleClinicInfo = useCallback(async () => {
    try {
      const token = await getToken();
      const { displayWelcomeModal } = await getUserSettings(token);
      const { hasRole, isAdmin } = getUserFromToken(token);
      const emailData = { email: "clinic@gmail.com" };

      // if user has no role, go to invalid page
      if (!hasRole) {
        navigate("/invalid");
      } else {
        let allClinicInfo: Array<ClinicType> = [];

        if (isAdmin) {
          allClinicInfo = await getClinics(token);
        } else {
          allClinicInfo = await login(token, emailData);
        }

        if (allClinicInfo && allClinicInfo?.length > 0) {
          const sortedClinics = allClinicInfo?.sort((a, b) =>
            a?.name?.localeCompare(b?.name)
          );

          // auto-select the first clinic when logging in
          setClinicId(sortedClinics?.[0].id ?? "");

          setWelcomeModal(displayWelcomeModal);

          // default page will be the dashboard page
          navigate("/dashboard");
        } else {
          // go to invalid page if any errors happen
          navigate("/invalid");
        }
      }
    } catch (error) {
      // go to invalid page if any errors happen
      navigate("/invalid");
    }
  }, [getToken, navigate, setClinicId, setWelcomeModal]);

  useEffect(() => {
    if (isLoading) return;

    const isCallbackRoute =
      location.pathname === "/callback" &&
      params.has("code") &&
      params.has("state");

    if (isAuthenticated) {
      handleClinicInfo();
    } else if (isCallbackRoute) {
      // isLoading is already false here, so Auth0Provider already attempted
      // the code/state exchange and it didn't result in authentication —
      // most commonly a stale, already-used code after a page refresh.
      // There's nothing to retry; send the user to the error page instead of
      // leaving them on this loader indefinitely.
      navigate("/invalid");
    } else {
      loginWithRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  return (
    <Box sx={{ ...PageContainerWrapper(theme), padding: 0 }}>
      <Loader />
    </Box>
  );
};

export default Callback;
